/* Beds & Bedding Store Platform data layer.
   Projection-first dual read: consume Shopify $app:store materialized projections when Liquid embedded them,
   otherwise fetch/cache/deduplicate the Public Store API. Operational consumers use BB.stores only. */
(function initStoreDataLayer() {
  'use strict';

  if (!window.BB) {
    document.addEventListener('bb:ready', initStoreDataLayer, { once: true });
    return;
  }
  if (window.BB.stores) return;

  var BB = window.BB;
  var STATES = Object.freeze({ PLATFORM: 'platform', UNLISTED: 'unlisted', UNAVAILABLE: 'unavailable' });
  var listCache = Object.create(null);
  var detailCache = Object.create(null);
  var projectionBySlug = Object.create(null);
  var projectionListComplete = false;
  var projectionNodesSeen = [];

  function isObject(value) { return value !== null && typeof value === 'object' && !Array.isArray(value); }
  function stringOrNull(value) { return typeof value === 'string' && value.trim() !== '' ? value : null; }
  function numberOrNull(value) {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim() !== '') {
      var parsed = Number(value);
      if (Number.isFinite(parsed)) return parsed;
    }
    return null;
  }
  function triState(value) { return value === true ? true : (value === false ? false : null); }

  function normalizeWindow(value) {
    if (!isObject(value)) return null;
    var openTime = stringOrNull(value.openTime);
    var closeTime = stringOrNull(value.closeTime);
    if (!openTime || !closeTime) return null;
    return { openTime: openTime, closeTime: closeTime };
  }

  function normalizeWindows(value) {
    if (!Array.isArray(value)) return null;
    var windows = [];
    for (var i = 0; i < value.length; i += 1) {
      var normalized = normalizeWindow(value[i]);
      if (!normalized) return null;
      windows.push(normalized);
    }
    return windows;
  }

  function normalizeAddress(value) {
    if (value === null || value === undefined) return null;
    if (!isObject(value)) return null;
    return {
      street: stringOrNull(value.street),
      houseNumber: stringOrNull(value.houseNumber),
      addressAddition: stringOrNull(value.addressAddition),
      postalCode: stringOrNull(value.postalCode),
      city: stringOrNull(value.city),
      province: stringOrNull(value.province),
      countryCode: stringOrNull(value.countryCode)
    };
  }

  function normalizeCoordinates(value) {
    if (value === null || value === undefined) return null;
    if (!isObject(value)) return null;
    var latitude = numberOrNull(value.latitude);
    var longitude = numberOrNull(value.longitude);
    if (latitude === null || longitude === null) return null;
    return { latitude: latitude, longitude: longitude };
  }

  function normalizeContact(value) {
    if (value === null || value === undefined) return null;
    if (!isObject(value)) return null;
    return { phone: stringOrNull(value.phone), email: stringOrNull(value.email) };
  }

  function normalizeToday(value) {
    if (value === null || value === undefined) return null;
    if (!isObject(value)) return null;
    var date = stringOrNull(value.date);
    var windows = normalizeWindows(value.windows);
    if (!date || windows === null) return null;
    return { date: date, windows: windows };
  }

  function normalizeRegularHours(value) {
    if (value === null || value === undefined) return null;
    if (!Array.isArray(value) || value.length !== 7) return null;
    var out = [];
    for (var i = 0; i < value.length; i += 1) {
      var row = value[i];
      if (!isObject(row)) return null;
      var weekday = stringOrNull(row.weekday);
      var windows = normalizeWindows(row.windows);
      if (!weekday || windows === null) return null;
      out.push({ weekday: weekday, windows: windows });
    }
    return out;
  }

  function normalizeUpcomingDays(value) {
    if (value === null || value === undefined) return null;
    if (!Array.isArray(value) || value.length !== 14) return null;
    var out = [];
    for (var i = 0; i < value.length; i += 1) {
      var day = value[i];
      if (!isObject(day)) return null;
      var date = stringOrNull(day.date);
      var weekday = stringOrNull(day.weekday);
      if (!date || !weekday) return null;
      out.push({
        date: date,
        weekday: weekday,
        closed: triState(day.closed),
        windows: normalizeWindows(day.windows),
        note: stringOrNull(day.note)
      });
    }
    return out;
  }

  function normalize(raw) {
    if (!isObject(raw)) return null;
    var slug = stringOrNull(raw.slug);
    if (!slug) return null;
    return {
      slug: slug,
      name: stringOrNull(raw.name),
      address: normalizeAddress(raw.address),
      coordinates: normalizeCoordinates(raw.coordinates),
      contact: normalizeContact(raw.contact),
      shopifyPageUrl: stringOrNull(raw.shopifyPageUrl),
      shortDescription: stringOrNull(raw.shortDescription),
      timezone: stringOrNull(raw.timezone),
      brands: Array.isArray(raw.brands) ? raw.brands.slice() : null,
      services: Array.isArray(raw.services) ? raw.services.slice() : null,
      bookingEnabled: triState(raw.bookingEnabled),
      openNow: triState(raw.openNow),
      today: normalizeToday(raw.today),
      regularHours: normalizeRegularHours(raw.regularHours),
      scheduleFrom: stringOrNull(raw.scheduleFrom),
      upcomingDays: normalizeUpcomingDays(raw.upcomingDays),
      projectedAt: stringOrNull(raw.projectedAt),
      contractVersion: stringOrNull(raw.contractVersion) || (typeof raw.contractVersion === 'number' ? String(raw.contractVersion) : null),
      productMatch: Object.prototype.hasOwnProperty.call(raw, 'productMatch') ? raw.productMatch : null
    };
  }

  function localClock(timezone) {
    if (!timezone || typeof Intl === 'undefined' || !Intl.DateTimeFormat) return null;
    try {
      var parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: timezone,
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
        weekday: 'long'
      }).formatToParts(new Date());
      var out = {};
      parts.forEach(function (part) { if (part.type !== 'literal') out[part.type] = part.value; });
      if (!out.year || !out.month || !out.day || !out.hour || !out.minute || !out.weekday) return null;
      return {
        date: out.year + '-' + out.month + '-' + out.day,
        time: out.hour + ':' + out.minute,
        weekday: out.weekday.toLowerCase()
      };
    } catch (_) { return null; }
  }

  function openAtTime(windows, time) {
    if (!Array.isArray(windows) || !time) return null;
    for (var i = 0; i < windows.length; i += 1) {
      var w = windows[i];
      if (w && typeof w.openTime === 'string' && typeof w.closeTime === 'string' && time >= w.openTime && time < w.closeTime) return true;
    }
    return false;
  }

  function materializeProjection(raw) {
    if (!isObject(raw)) return null;
    var projected = Object.assign({}, raw);
    var clock = localClock(stringOrNull(raw.timezone));
    var upcoming = Array.isArray(raw.upcomingDays) ? raw.upcomingDays : null;
    var regular = Array.isArray(raw.regularHours) ? raw.regularHours : null;
    var today = null;
    var openNow = null;
    var projectionState = 'projection-stale';

    if (clock && upcoming) {
      for (var i = 0; i < upcoming.length; i += 1) {
        if (upcoming[i] && upcoming[i].date === clock.date) {
          projectionState = 'projection';
          var day = upcoming[i];
          if (day.closed === true) {
            today = { date: clock.date, windows: [] };
            openNow = false;
          } else if (day.closed === false && Array.isArray(day.windows)) {
            today = { date: clock.date, windows: day.windows };
            openNow = openAtTime(day.windows, clock.time);
          }
          break;
        }
      }
    }

    if (!today && clock && regular) {
      for (var j = 0; j < regular.length; j += 1) {
        if (regular[j] && regular[j].weekday === clock.weekday && Array.isArray(regular[j].windows)) {
          today = { date: clock.date, windows: regular[j].windows };
          openNow = openAtTime(regular[j].windows, clock.time);
          break;
        }
      }
    }

    projected.today = today;
    projected.openNow = openNow;
    var normalized = normalize(projected);
    if (normalized) normalized.projectionState = projectionState;
    return normalized;
  }

  function ingestProjectionNode(node) {
    if (!node || projectionNodesSeen.indexOf(node) !== -1) return;
    projectionNodesSeen.push(node);
    try {
      var payload = JSON.parse(node.textContent || 'null');
      var rows = Array.isArray(payload) ? payload : [payload];
      rows.forEach(function (raw) {
        var store = materializeProjection(raw);
        if (store && store.slug) projectionBySlug[store.slug] = store;
      });
      if (node.getAttribute('data-bb-store-projection-list') === 'complete') projectionListComplete = true;
    } catch (_) {}
  }

  function hydrateProjectionCache() {
    if (!document || !document.querySelectorAll) return;
    Array.prototype.forEach.call(document.querySelectorAll('script[data-bb-store-projection-single],script[data-bb-store-projection-list]'), ingestProjectionNode);
  }

  function normalizeContext(value) {
    value = isObject(value) ? value : {};
    var out = {};
    var productGid = stringOrNull(value.productGid);
    var brand = stringOrNull(value.brand);
    if (productGid) out.productGid = productGid;
    if (brand) out.brand = brand;
    return out;
  }

  function listPath(context) {
    var c = normalizeContext(context);
    var query = [];
    if (c.productGid) query.push('productGid=' + encodeURIComponent(c.productGid));
    if (c.brand) query.push('brand=' + encodeURIComponent(c.brand));
    return '/public/v1/stores' + (query.length ? '?' + query.join('&') : '');
  }

  function publicError(error) {
    var status = error && typeof error.status === 'number' ? error.status : null;
    var code = error && error.body && error.body.error && typeof error.body.error.code === 'string' ? error.body.error.code : null;
    return { status: status, code: code };
  }

  function unavailableList(error) { return { state: STATES.UNAVAILABLE, stores: [], error: publicError(error) }; }
  function unavailableOne(error) { return { state: STATES.UNAVAILABLE, store: null, error: publicError(error) }; }

  function fetchList(context) {
    hydrateProjectionCache();
    var normalizedContext = normalizeContext(context);
    if (projectionListComplete && !normalizedContext.productGid && !normalizedContext.brand) {
      var projectedStores = Object.keys(projectionBySlug).map(function (slug) { return projectionBySlug[slug]; });
      var listContentState = projectedStores.some(function (store) { return store.projectionState === 'projection-stale'; }) ? 'projection-stale' : 'projection';
      return Promise.resolve({ state: STATES.PLATFORM, stores: projectedStores, error: null, source: 'projection', contentState: listContentState });
    }
    var path = listPath(context);
    if (listCache[path]) return listCache[path];
    var url = BB.platformUrl(path);
    if (!url) return Promise.resolve(unavailableList(null));

    var promise = BB.fetchJSON(url).then(function (data) {
      if (!data || !Array.isArray(data.stores)) throw new Error('malformed-store-list');
      var stores = [];
      for (var i = 0; i < data.stores.length; i += 1) {
        var store = normalize(data.stores[i]);
        if (store) stores.push(store);
      }
      return { state: STATES.PLATFORM, stores: stores, error: null, source: 'api', contentState: 'missing' };
    }).catch(function (error) {
      delete listCache[path];
      return unavailableList(error);
    });

    listCache[path] = promise;
    return promise;
  }

  function fetchOne(slug) {
    slug = stringOrNull(slug);
    if (!slug) return Promise.resolve({ state: STATES.UNLISTED, store: null, error: null });
    hydrateProjectionCache();
    if (projectionBySlug[slug]) return Promise.resolve({ state: STATES.PLATFORM, store: projectionBySlug[slug], error: null, source: 'projection', contentState: projectionBySlug[slug].projectionState || 'projection-stale' });
    if (detailCache[slug]) return detailCache[slug];
    var url = BB.platformUrl('/public/v1/stores/' + encodeURIComponent(slug));
    if (!url) return Promise.resolve(unavailableOne(null));

    var promise = BB.fetchJSON(url).then(function (data) {
      var store = normalize(data && data.store);
      if (!store) throw new Error('malformed-store-detail');
      return { state: STATES.PLATFORM, store: store, error: null, source: 'api', contentState: 'missing' };
    }).catch(function (error) {
      if (error && error.status === 404) return { state: STATES.UNLISTED, store: null, error: null };
      delete detailCache[slug];
      return unavailableOne(error);
    });

    detailCache[slug] = promise;
    return promise;
  }

  function formatWindows(windows) {
    var normalized = normalizeWindows(windows);
    if (normalized === null) return { state: 'unknown', text: BB.t('storeHoursUnavailable') };
    if (normalized.length === 0) return { state: 'closed', text: BB.t('closed') };
    return { state: 'open', text: normalized.map(function (w) { return w.openTime + ' – ' + w.closeTime; }).join(', ') };
  }

  function formatOpenState(store) {
    var openNow = store ? triState(store.openNow) : null;
    if (openNow === null) return { state: 'unknown', text: BB.t('storeStatusUnknown') };
    var projected = BB.storeOpenState(store);
    return { state: projected.state, text: projected.text };
  }

  function formatToday(store) {
    if (!store || !store.today) return { state: 'unknown', text: BB.t('storeHoursUnavailable') };
    var formatted = formatWindows(store.today.windows);
    if (formatted.state === 'open') return formatted;
    if (formatted.state === 'closed' && store.openNow === false) return { state: 'closed', text: BB.t('closedToday') };
    return { state: 'unknown', text: BB.t('storeHoursUnavailable') };
  }

  function formatRegularHours(regularHours) {
    if (regularHours === null || !Array.isArray(regularHours)) return null;
    return regularHours.map(function (row) {
      var formatted = formatWindows(row.windows);
      return { weekday: row.weekday, state: formatted.state, text: formatted.text };
    });
  }

  function formatUpcomingDay(day) {
    if (!day) return { state: 'unknown', text: BB.t('storeHoursUnavailable'), note: null };
    if (day.closed === null) return { state: 'unknown', text: BB.t('storeHoursUnavailable'), note: day.note || null };
    if (day.closed === true) return { state: 'closed', text: BB.t('closed'), note: day.note || null };
    var formatted = formatWindows(day.windows);
    if (formatted.state === 'closed') formatted = { state: 'unknown', text: BB.t('storeHoursUnavailable') };
    return { state: formatted.state, text: formatted.text, note: day.note || null };
  }

  function formatResultState(result) {
    var state = result && result.state;
    if (state === STATES.PLATFORM) return { state: STATES.PLATFORM, text: '' };
    if (state === STATES.UNLISTED) return { state: STATES.UNLISTED, text: BB.t('storeUnlisted') };
    return { state: STATES.UNAVAILABLE, text: BB.t('storeUnavailable') };
  }

  function addressParts(address) {
    if (!address) return { line1: null, line2: null, singleLine: null };
    var first = [address.street, address.houseNumber, address.addressAddition].filter(Boolean).join(' ');
    var second = [address.postalCode, address.city].filter(Boolean).join(' ');
    var lines = [first, second].filter(Boolean);
    return { line1: first || null, line2: second || null, singleLine: lines.length ? lines.join(', ') : null };
  }

  function routeUrl(store) {
    if (!store) return null;
    var destination = null;
    if (store.coordinates) destination = store.coordinates.latitude + ',' + store.coordinates.longitude;
    if (!destination) destination = addressParts(store.address).singleLine;
    return destination ? 'https://www.google.com/maps/dir/?api=1&destination=' + encodeURIComponent(destination) : null;
  }

  function telHref(store) {
    var phone = store && store.contact ? store.contact.phone : null;
    if (!phone) return null;
    var normalized = phone.replace(/[^+0-9]/g, '');
    return normalized ? 'tel:' + normalized : null;
  }

  function emailHref(store) {
    var email = store && store.contact ? store.contact.email : null;
    return email ? 'mailto:' + email : null;
  }

  BB.stores = Object.freeze({
    states: STATES,
    fetchList: fetchList,
    fetchOne: fetchOne,
    normalize: normalize,
    normalizeContext: normalizeContext,
    format: Object.freeze({
      windows: formatWindows,
      openState: formatOpenState,
      today: formatToday,
      regularHours: formatRegularHours,
      upcomingDay: formatUpcomingDay,
      resultState: formatResultState,
      address: addressParts,
      routeUrl: routeUrl,
      telHref: telHref,
      emailHref: emailHref
    })
  });
})();
